import { motion } from "framer-motion";

const GOLD = "#F5C518";

// Rows mirror the owner's reference image (left → right): 3 / 4 / 5 / 4 cells.
const ROWS = [
  [
    { id: "crewznation", src: "/logos/crewznation-hero.png?v=2", alt: "CrewZ Nation", h: 0.9 },
    { id: "fahrers", src: "/logos/fahrers-hero.png", alt: "The Fahrers", h: 0.62 },
    { id: "sms", src: "/logos/sms.png?v=3", alt: "SMS", h: 0.72 },
  ],
  [
    { id: "incas", src: "/logos/incas.png?v=3", alt: "Incas", h: 1, invert: true },
    { id: "sto", src: "/logos/sto-performance.png?v=8", alt: "STO Performance", h: 1 },
    { id: "siyak", src: "/logos/siyak.png?v=6", alt: "Siyak Alruyah Alalamiyah", h: 1 },
    { id: "almurad", src: "/logos/almurad.png?v=2", alt: "Almurad — Specialists in Rim Repair", h: 1 },
  ],
  [
    { id: "momo", src: "/logos/sponsors/momo.png?v=1", alt: "Momo", h: 1 },
    { id: "palamas", src: "/logos/sponsors/palamas.png?v=1", alt: "Palamas", h: 1 },
    { id: "atlas", src: "/logos/sponsors/atlas-speciality.png?v=1", alt: "Atlas Speciality", h: 0.78 },
    { id: "k2", src: "/logos/sponsors/k2.png?v=3", alt: "K2", h: 1 },
    { id: "gashlan", src: "/logos/sponsors/gashlan-carz.png?v=1", alt: "Gashlan Carz", h: 1.1 },
  ],
  [
    { id: "envision", src: "/logos/sponsors/envision-motorsport.png?v=1", alt: "EM Envision Motorsport", h: 0.92 },
    { id: "h10", src: "/logos/sponsors/h10.png?v=1", alt: "H10", h: 0.5 },
    { id: "indian", src: "/logos/sponsors/indian-motorcycle-riders.png?v=1", alt: "Indian Motorcycle Riders", h: 1.1 },
    { id: "order-your-parts", src: "/logos/sponsors/order-your-parts-ref.png?v=1", alt: "Order Your Parts", h: 1 },
  ],
];

const RuleHeading = ({ children, wide, testId }) => (
  <div className={`flex items-center justify-center gap-4 w-full ${wide ? "" : "max-w-[300px] sm:max-w-[360px]"}`} data-testid={testId}>
    <span className="h-px flex-1" style={{ background: GOLD }} />
    <span
      className="font-heading font-bold uppercase whitespace-nowrap text-lg sm:text-2xl tracking-[0.12em]"
      style={{ color: GOLD, fontFamily: "'Outfit', sans-serif" }}
    >
      {children}
    </span>
    <span className="h-px flex-1" style={{ background: GOLD }} />
  </div>
);

const LogoCell = ({ logo, cols, first }) => (
  <div
    className={`flex items-center justify-center px-3 sm:px-5 h-[64px] sm:h-[96px] lg:h-[118px] ${
      cols === 3 ? "basis-1/2 sm:basis-1/3" : cols === 4 ? "basis-1/2 sm:basis-1/4" : "basis-1/3 sm:basis-1/5"
    } ${first ? "" : "sm:border-l sm:border-white/60"}`}
    data-testid={`partner-cell-${logo.id}`}
  >
    <img
      src={logo.src}
      alt={logo.alt}
      loading="lazy"
      className="max-w-full w-auto object-contain"
      style={{ height: `${logo.h * 100}%`, filter: logo.invert ? "invert(1)" : undefined }}
      data-testid={`partner-logo-${logo.id}`}
    />
  </div>
);

export const AuthorityRow = () => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, delay: 0.65 }}
    className="flex flex-row items-center justify-center gap-5 sm:gap-8 mt-6"
    data-testid="authority-row"
  >
    <img src="/logos/sponsors/ministry-of-sport.png?v=1" alt="Ministry of Sport" className="h-[104px] sm:h-[156px] w-auto object-contain" data-testid="authority-logo-ministry" />
    <div className="h-[80px] sm:h-[124px] w-px bg-white/70" data-testid="authority-divider" />
    <img src="/logos/sponsors/saudi-motor-federation.png?v=1" alt="Saudi Automobile & Motorcycle Federation" className="h-[92px] sm:h-[147px] max-w-[46vw] w-auto object-contain" data-testid="authority-logo-samf" />
  </motion.div>
);

export const SponsorLineup = () => (
  <div className="flex flex-col items-center w-full" data-testid="sponsor-lineup">
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.75 }}
      className="flex flex-col items-center gap-3 mt-2 mb-6 w-full"
      data-testid="venue-block"
    >
      <RuleHeading testId="venue-heading">VENUE</RuleHeading>
      <div className="flex flex-row items-center justify-center gap-4 sm:gap-6 max-w-full">
        <img src="/logos/kinan-combined.png?v=1" alt="Kinan" className="h-[54px] sm:h-[84px] w-auto object-contain" style={{ filter: "invert(1)" }} data-testid="venue-logo-kinan" />
        <div className="h-[46px] sm:h-[74px] w-px bg-white/70" data-testid="venue-divider" />
        <img src="/logos/rimal-only.png?v=1" alt="Rimal Center" className="h-[54px] sm:h-[84px] w-auto object-contain" style={{ filter: "invert(1)" }} data-testid="venue-logo-rimal" />
      </div>
    </motion.div>

    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.85 }}
      className="flex flex-col items-center gap-2 sm:gap-4 mb-6 w-full max-w-[1200px]"
      data-testid="partner-logos-row"
    >
      <RuleHeading wide testId="partners-heading">PARTNERS • SPONSORS</RuleHeading>
      {ROWS.map((row, r) => (
        <div key={r} className="flex flex-row flex-wrap justify-center w-full gap-y-3" data-testid={`partner-row-${r + 1}`}>
          {row.map((logo, i) => (
            <LogoCell key={logo.id} logo={logo} cols={row.length} first={i === 0} />
          ))}
        </div>
      ))}
    </motion.div>
  </div>
);
